import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Check, MessageCircle, Send, Phone, Share2, ExternalLink } from "lucide-react";

const VIBER_ICON = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M11.4 0C9.473.028 5.333.344 3.014 2.467 1.294 4.187.525 6.67.41 9.733.295 12.797.068 18.467 5.45 19.933v2.253s-.036.912.563 1.098c.724.227 1.15-.467 1.843-1.22.378-.413.901-.998 1.298-1.46 3.579.302 6.327-.387 6.639-.497.72-.254 4.792-.756 5.455-6.163.685-5.572-.333-9.09-2.197-10.687 0 0-.058-.048-.162-.12C17.56 2.118 14.596.014 11.4 0zm.322 1.67c2.745-.008 5.357 1.746 6.275 2.4 1.52 1.303 2.397 4.403 1.81 9.167-.537 4.367-3.78 4.724-4.378 4.936-.258.09-2.655.682-5.65.504 0 0-2.24 2.707-2.94 3.42-.11.113-.247.156-.336.137-.126-.03-.16-.176-.158-.39l.025-3.72C2.318 17.05 2.05 12.3 2.14 9.845c.093-2.533.698-4.615 2.12-6.023C5.97 2.151 9.317 1.683 11.722 1.67z"/><path d="M12.246 5.24c-.186 0-.186.288 0 .29 1.094.009 2.12.455 2.892 1.22.773.766 1.21 1.79 1.228 2.885.002.186.29.183.288-.003a4.42 4.42 0 0 0-1.302-3.061 4.42 4.42 0 0 0-3.106-1.33zm.032 1.21c-.184.003-.18.29.003.29.667.015 1.29.285 1.758.76.468.476.73 1.103.737 1.77.001.186.289.184.288-.002a2.9 2.9 0 0 0-.854-2.047 2.88 2.88 0 0 0-1.932-.77zm.043 1.233c-.183.009-.17.296.012.29a1.39 1.39 0 0 1 1.392 1.414c.003.186.29.18.288-.005a1.68 1.68 0 0 0-.48-1.153 1.68 1.68 0 0 0-1.212-.546zm-1.674 1.12c.248-.012.476.08.732.314l.002.002c.275.25.52.528.73.83.14.195.213.42.14.638-.065.191-.22.368-.45.552l-.006.005c-.164.128-.293.31-.257.51.19 1.07.722 1.943 1.5 2.65.015.014.03.026.047.037.465.35.997.607 1.564.754.17.04.375-.02.554-.153l.007-.006c.218-.167.396-.323.576-.387.205-.073.425-.008.616.144l.001.001c.32.235.623.502.898.796.187.198.23.438.124.675-.15.335-.526.66-.965.87-.44.21-.931.3-1.277.192-1.26-.396-2.381-1.14-3.376-2.015a10.03 10.03 0 0 1-1.857-2.262c-.41-.695-.742-1.434-.766-2.224-.015-.472.173-.773.48-.959.18-.11.33-.167.482-.173z"/></svg>
);

export default function ShareDialog({ open, onOpenChange, shareUrl, title, description, type }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = shareUrl || window.location.href;
  const shareText = `${title || "Check this out on SetPlay"}${description ? ` - ${description}` : ""}`;
  const encodedUrl = encodeURIComponent(fullUrl);
  const encodedText = encodeURIComponent(shareText);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const input = document.createElement("input");
      input.value = fullUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: title || "SetPlay", text: shareText, url: fullUrl });
      } catch {}
    }
  };

  const channels = [
    { name: "WhatsApp", icon: MessageCircle, color: "bg-green-500 hover:bg-green-600 text-white",
      url: `https://wa.me/?text=${encodedText}%20${encodedUrl}` },
    { name: "Messenger", icon: Send, color: "bg-blue-500 hover:bg-blue-600 text-white",
      url: `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=0&redirect_uri=${encodedUrl}` },
    { name: "Viber", icon: VIBER_ICON, color: "bg-purple-600 hover:bg-purple-700 text-white",
      url: `viber://forward?text=${encodedText}%20${encodedUrl}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <Share2 className="w-5 h-5 text-court-orange" />Share Link
          </DialogTitle>
          <DialogDescription>{title || "Share this with your group"}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Copy Link */}
          <div className="flex gap-2">
            <Input data-testid="share-url-input" value={fullUrl} readOnly className="h-11 rounded-none border-2 text-sm font-mono bg-slate-50" />
            <Button data-testid="copy-link-btn" onClick={handleCopy}
              className={`px-4 rounded-full font-heading uppercase text-xs shrink-0 ${copied ? 'bg-lime-volt text-black' : 'bg-court-orange hover:bg-court-orange-hover text-white'} shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]`}>
              {copied ? <><Check className="w-4 h-4 mr-1" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
            </Button>
          </div>

          {/* Share Channels */}
          <div>
            <p className="text-xs font-heading uppercase tracking-[0.15em] text-muted-foreground mb-3">Share via</p>
            <div className="grid grid-cols-3 gap-2">
              {channels.map(ch => (
                <a key={ch.name} href={ch.url} target="_blank" rel="noopener noreferrer" data-testid={`share-${ch.name.toLowerCase()}`}
                  className={`flex items-center justify-center gap-2 py-3 rounded-full text-sm font-heading uppercase tracking-wide transition-colors ${ch.color}`}>
                  {typeof ch.icon === 'function' ? <ch.icon /> : <ch.icon className="w-5 h-5" />}
                  {ch.name}
                </a>
              ))}
            </div>
          </div>

          {/* Native Share (mobile) */}
          {navigator.share && (
            <Button data-testid="native-share-btn" onClick={handleNativeShare} variant="outline"
              className="w-full rounded-full border-2 font-heading uppercase text-xs">
              <ExternalLink className="w-4 h-4 mr-2" />More sharing options
            </Button>
          )}

          {/* Link Preview */}
          <div className="bg-slate-50 border border-slate-200 p-3">
            <p className="text-[10px] font-heading uppercase tracking-[0.15em] text-muted-foreground mb-1">Preview</p>
            <p className="font-medium text-sm">{title || "SetPlay"}</p>
            {description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{description}</p>}
            <p className="text-[10px] text-court-orange mt-1 truncate">{fullUrl}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
